'use client';

import React, { useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

import {
  campaignTitleParamName,
  moduleOriginParamName,
  sessionProductViewsKey,
} from '@/modules/activityTracker/constants';
import { createTrackingId } from '@/modules/activityTracker/useActivityTracker';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking';
import { getLocalStorageItem, setLocalStorageItem } from '@/modules/storage';
import { safeJsonParse } from '@/modules/storage/helpers';

export function ProductTracking() {
  const searchParams = useSearchParams();
  const { data: product } = useProductBySlugBasicPDP();
  const { data: productExtended } = useProductBySlugExtendedPDP();
  const {
    sendProductViewATEvent,
    sendProductViewGTMEvent,
    sendProductViewedSegmentedEvent,
    sendEngagedSessionActionATEvent,
    sendWebModuleAttributionProductViewATEvent,
  } = useProductTracking();

  const moduleOrigin = Array.isArray(searchParams.get(moduleOriginParamName))
    ? (searchParams.get(moduleOriginParamName) as string[][0])
    : searchParams.get(moduleOriginParamName);

  const campaignTitle = searchParams.get(campaignTitleParamName);

  useEffect(() => {
    if (product && productExtended) {
      const eventId = createTrackingId();

      sendProductViewedSegmentedEvent(product, productExtended);
      sendProductViewATEvent(
        product.id,
        eventId,
        productExtended.pricing,
        product.status
      );
      const singleVariantId =
        product.variants_all?.length === 1
          ? product.variants_all[0].id
          : undefined;
      sendProductViewGTMEvent(
        product.id,
        productExtended.pricing,
        eventId,
        product.attributes.product_type,
        singleVariantId
      );
    }
  }, [productExtended?.pricing, product?.id]);

  useEffect(() => {
    if (product?.id) {
      const sessionProductViews = getLocalStorageItem(sessionProductViewsKey);
      const parsedSessionProductViews = safeJsonParse(sessionProductViews, []);

      if (parsedSessionProductViews.length < 2) {
        const updatedSessionProductViews = Array.from(
          new Set([...parsedSessionProductViews, product?.id])
        );
        setLocalStorageItem(
          sessionProductViewsKey,
          JSON.stringify(updatedSessionProductViews)
        );
        if (updatedSessionProductViews.length === 2) {
          sendEngagedSessionActionATEvent();
        }
      }
    }
  }, [product?.id]);

  useEffect(() => {
    if (product?.id && moduleOrigin) {
      sendWebModuleAttributionProductViewATEvent(
        product.id,
        moduleOrigin,
        campaignTitle
      );
    }
  }, [moduleOrigin, product?.id]);

  return <></>;
}
